import { useIntegrationCapabilityResolver } from '@/hooks/integrations/useIntegrationCapabilityResolver';
import { useIntegrationLaunchSource } from '@/hooks/integrations/useIntegrationLaunchSource';
import { useIntegrationOnboardingSessionResolver } from '@/hooks/integrations/useIntegrationOnboardingSessionResolver';
import { useLegacyIntegrationSource } from '@/hooks/integrations/useLegacyIntegrationSource';
import { isEhrIntegrationProvider } from '@heidi/integrations/connected-apps/contracts';
import { getIntegrationSyncPolicy } from '@heidi/integrations/ehr/data';
import { type EHRProvider } from '@heidi/schema/ml-scribe';
import { useMemo } from 'react';

/** `@deprecated` Use IntegrationLaunchSource instead */
export enum IntegrationConnectionType {
  EHR_PROFILE_OLD = 'EHR_PROFILE_OLD',
  EHR_LAUNCH_PROFILE = 'EHR_LAUNCH_PROFILE',
  INTEGRATION_ONBOARDING_SESSION = 'INTEGRATION_ONBOARDING_SESSION',
}

export enum IntegrationLaunchSource {
  NONE = 'NONE',
  LEGACY_EHR_PROFILE = 'LEGACY_EHR_PROFILE',
  LEGACY_EPIC_FHIR_AUTH = 'LEGACY_EPIC_FHIR_AUTH',
  INTEGRATION_ONBOARDING_SESSION = 'INTEGRATION_ONBOARDING_SESSION',
  GENERIC_EHR_LAUNCH = 'GENERIC_EHR_LAUNCH',
}

export const useIntegrationEhrInfo = (ehrProviderFromSource?: EHRProvider | null) => {
  const {
    userEhrProvider,
    hasLegacyEhrProfile,
    isLegacySourcePending,
    isLegacySourceError,
    isLegacySourceLoading,
    isLegacySourceLoadingError,
  } = useLegacyIntegrationSource();

  const {
    integrationOnboardingSessions,
    integrationOnboardingSession,
    selectedOnboardingProvider,
    isOnboardingSessionPending,
    isOnboardingSessionError,
    isOnboardingSessionLoading,
    isOnboardingSessionLoadingError,
    isIntegrationOnboardingSession,
  } = useIntegrationOnboardingSessionResolver(ehrProviderFromSource);

  const {
    integrationLaunchSource,
    isLegacyEpicFhirAuth,
    isGenericEhrLaunch,
    genericLaunchEhrProvider,
  } = useIntegrationLaunchSource({
    hasIntegrationOnboardingSession: Boolean(isIntegrationOnboardingSession),
    hasLegacyEhrProfile,
  });

  const { integrationCapabilities, isCapabilitiesPending, isCapabilitiesError } =
    useIntegrationCapabilityResolver({
      isGenericEhrLaunch,
      genericLaunchEhrProvider,
      onboardingSessionProvider: selectedOnboardingProvider,
    });

  const isPending = isLegacySourcePending || isOnboardingSessionPending;
  // `isPending` is also true forever while a source query is disabled, so
  // "actually fetching" is `isLoading`. And `isError` also fires on a failed
  // background refetch with cached data still present (TanStack v5 keeps the
  // data); only an initial load that never succeeded is a loading error, so a
  // network blip on focus refetch does not un-settle the EHR info. Consumers
  // that must fail closed on any error use `isError`.
  const isLoading = isLegacySourceLoading || isOnboardingSessionLoading;
  const isLoadingError = isLegacySourceLoadingError || isOnboardingSessionLoadingError;
  // `isCapabilitiesPending` stays separate from `isPending`: the capability query
  // resolves AFTER the source queries, and it is what produces the canSync*
  // flags — surfaced on its own so a consumer can fail closed on those flags
  // without widening `isPending` for every other caller.
  const isError = isLegacySourceError || isOnboardingSessionError;

  const integrationConnectionType = useMemo(() => {
    switch (true) {
      case isIntegrationOnboardingSession:
        return IntegrationConnectionType.INTEGRATION_ONBOARDING_SESSION;
      case hasLegacyEhrProfile:
        return IntegrationConnectionType.EHR_PROFILE_OLD;
      default:
        return undefined;
    }
  }, [isIntegrationOnboardingSession, hasLegacyEhrProfile]);

  const integrationEhrProvider = useMemo(() => {
    switch (true) {
      case isGenericEhrLaunch:
        return genericLaunchEhrProvider;
      case isIntegrationOnboardingSession:
        return integrationOnboardingSession?.provider;
      case hasLegacyEhrProfile:
        return userEhrProvider;
      default:
        return undefined;
    }
  }, [
    hasLegacyEhrProfile,
    isGenericEhrLaunch,
    isIntegrationOnboardingSession,
    genericLaunchEhrProvider,
    integrationOnboardingSession?.provider,
    userEhrProvider,
  ]);

  const integrationOnboardingSessionId = integrationOnboardingSession?.id;

  // Published as PostHog person properties / Flagsmith traits for flag
  // targeting (see auth.tsx and openfeature-context-sync.tsx). What this
  // guarantees: all three always come from the SAME session document, and all
  // three are absent together when there is no qualifying onboarding session.
  // A release condition ANDing the provider with both timestamps is only
  // meaningful under that invariant.
  //
  // Deliberately NOT tied to which source won `integrationEhrProvider` above.
  // `isIntegrationOnboardingSession` is independent of `isGenericEhrLaunch`, so
  // a user in a generic EHR launch who also has a qualifying onboarding session
  // still reports these — `integrationEhrProvider` will be the launch provider
  // while these describe the session. That is intended: the targeting condition
  // reads only these keys, never `ehr_provider`, so keying off the session is
  // what identifies the cohort. Narrowing to "the session won the switch" would
  // drop legitimate cohort members who happen to be in a launch session.
  const integrationOnboardingCohort = useMemo(() => {
    if (!isIntegrationOnboardingSession || !integrationOnboardingSession) {
      return {
        provider: undefined,
        createdAt: undefined,
        updatedAt: undefined,
      };
    }

    return {
      provider: integrationOnboardingSession.provider as EHRProvider,
      createdAt: integrationOnboardingSession.created_at,
      updatedAt: integrationOnboardingSession.updated_at,
    };
  }, [isIntegrationOnboardingSession, integrationOnboardingSession]);

  // Determine if users have multiple EHR providers based on onboarding sessions and old EHR profile
  const hasMultiple = useMemo(() => {
    if (!integrationOnboardingSessions?.length && !userEhrProvider) return false;

    const uniqueProviders = new Set(
      integrationOnboardingSessions
        ?.map((session) => session.provider)
        .filter(isEhrIntegrationProvider)
    );

    if (userEhrProvider) {
      uniqueProviders.add(userEhrProvider);
    }

    return uniqueProviders.size > 1;
  }, [integrationOnboardingSessions, userEhrProvider]);

  const { canSyncSchedule, canSyncNotes, canSyncSectionalNotes } =
    getIntegrationSyncPolicy({
      hasLegacyEhrProfile,
      hasMultiple,
      isLegacyEpicFhirAuth,
      supportsGetSchedule: integrationCapabilities?.supports_get_schedule,
      supportsSyncNotes: integrationCapabilities?.supports_sync_notes,
      supportsSyncSectionalNotes: integrationCapabilities?.supports_sync_sectional_notes,
    });

  const canSyncDocuments = Boolean(integrationCapabilities?.supports_sync_document);

  return useMemo(
    () => ({
      integrationConnectionType,
      isIntegrationOnboardingSession,
      integrationLaunchSource,
      integrationEhrProvider: integrationEhrProvider as EHRProvider | undefined,
      integrationOnboardingSessionId,
      integrationOnboardingProvider: integrationOnboardingCohort.provider,
      integrationOnboardingCreatedAt: integrationOnboardingCohort.createdAt,
      integrationOnboardingUpdatedAt: integrationOnboardingCohort.updatedAt,
      isPending,
      isLoading,
      isCapabilitiesPending,
      isCapabilitiesError,
      isError,
      isLoadingError,
      canSyncSchedule,
      canSyncNotes,
      canSyncSectionalNotes,
      canSyncDocuments,
      hasMultiple,
    }),
    [
      canSyncNotes,
      canSyncSchedule,
      canSyncSectionalNotes,
      canSyncDocuments,
      hasMultiple,
      integrationConnectionType,
      isIntegrationOnboardingSession,
      integrationLaunchSource,
      integrationEhrProvider,
      integrationOnboardingSessionId,
      integrationOnboardingCohort,
      isError,
      isPending,
      isLoading,
      isCapabilitiesPending,
      isCapabilitiesError,
      isLoadingError,
    ]
  );
};
